import {HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse} from '@angular/common/http';
import {inject, PLATFORM_ID} from '@angular/core';
import {catchError, of, switchMap, tap, throwError, timeout} from 'rxjs';
import {isPlatformBrowser} from '@angular/common';
import {environment} from '../../../environments/environment';
import {AuthRefreshService, ServiceTokenService, SubscribersService, ToastService, TokenStorageService} from '@coreServices';
import {ApiConstants, CommonConstants} from '@coreConstant';
import {SKIP_ERROR_TOAST} from './http-context.tokens';

/** Pre-isolation cookie name, read only as a transition fallback. TRANSITION - DELETE ME. */
const CSRF_COOKIE_LEGACY = 'XSRF-TOKEN';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenStorageService = inject(TokenStorageService);
  const serviceTokenService = inject(ServiceTokenService);
  const authRefreshService = inject(AuthRefreshService);
  const platformId = inject(PLATFORM_ID);
  const toastService = inject(ToastService);
  const subscriberService = inject(SubscribersService);
  const reqUrl = req.url;
  const pathsNodeAPI = ['auth/session', 'auth/google', 'auth/logout', 'auth/refresh-token', 'auth/forgot-password', 'auth/reset-password', 'auth/change-password', 'auth/email-verification', 'auth/me', 'auth/service-token', 'auth/profile', 'auth/register', 'quote', 'quote-delivery-address', 'quote-product', 'status', 'quote-summary', 'shipping-calculation', 'quote-schedule-delivery', 'quote-schedule-delivery-product', 'product/lead-time', 'address-type'];
  const pathsNodeAPINot = ['quote-product-applied-calculator', 'quote-order/delete-all-quote-product-applied-calculators/', 'quote-order/delete-all-quote-product-applied-calculators-by-export-id/', 'quote-payment', 'quote-order-payment-receipt', 'quote-order/status-update-by-export-id', 'quote-feedback', 'quote-order/download', 'quote-order/download-by-export-id'];

  // Special handling for live link quote retrieval - route to Node API for consistency
  const isLiveLinkQuoteRetrieval = reqUrl.startsWith('quote/export/') && reqUrl.includes('/');

  const isNodeAPI = (pathsNodeAPI.some(route => reqUrl.startsWith(route)) && !pathsNodeAPINot.some(route => reqUrl.startsWith(route))) || isLiveLinkQuoteRetrieval;
  req = req.clone({
    url: (isNodeAPI ? environment.nodeApiUrl : environment.apiUrl) + req.url
  });

  // Prevent browser from caching product API responses by appending a timestamp
  if (reqUrl.startsWith('product')) {
    const separator = req.url.includes('?') ? '&' : '?';
    req = req.clone({
      url: req.url + separator + '_t=' + Date.now(),
    });
  }

  // Silent auth probes: a 401/failure here just means "not logged in" (e.g. the boot /me check for
  // a guest), so it must not raise the global error toast.
  // Both the PHP endpoints and their Node replacements, so moving one across does not start
  // raising a toast at a guest who is simply not signed in.
  //
  // `auth/refresh-token` belongs here too, and its absence was visible to users. A renewal that
  // fails is never itself news: for a signed-in person the ORIGINAL request reports the expiry
  // (see `report` below, which is called with that request's error), and for a guest there was no
  // session to expire. Because the renewal is an ordinary HttpClient call it ran through this
  // interceptor, was refresh-exempt, and so reported its own 401 — which is why simply opening a
  // public page as a guest (a share link, the reset-password screen) popped "Your session has
  // expired. login again." at someone who had never signed in.
  const isSilentAuthProbe = reqUrl.startsWith('auth/me')
    || reqUrl.startsWith('auth/service-token')
    || reqUrl.startsWith('auth/refresh-token');
  // The card-payment return check reports its own outcome (PaymentResponseComponent), including a
  // return with no card payment — BPAY chosen on the payment page — which is not an error to the
  // customer. A global toast on top of that read as a failed payment.
  const isPaymentReturnCheck = reqUrl.startsWith(ApiConstants.QUOTE_ORDER_PAYMENT_VALIDATE_TOKEN_URL);
  // The caller handles this request's failure itself (see SKIP_ERROR_TOAST).
  const skipErrorToast = isSilentAuthProbe || isPaymentReturnCheck || req.context.get(SKIP_ERROR_TOAST);

  // On the server (prerender + SSR), a hung API call blocks app stability until the build's
  // hard 30s page limit kills the entire prerender worker pool. Fail the single request after
  // 15s instead: the page still renders (without that data) and the client re-fetches on hydration.
  const SSR_REQUEST_TIMEOUT_MS = 15000;

  // Requests that must never trigger a refresh. The refresh call itself would recurse; the login
  // call has no session to renew and its 401 is the user's own bad password.
  //
  // `oauth/login-user` is deliberately NOT on this list, and that matters more than it looks. It is
  // the bootstrap "am I signed in?" probe, and with a ~15-minute access token a returning visitor
  // is ALWAYS in the state where it 401s while their session is still perfectly renewable. Excluding
  // it would mean the 30-day session quietly behaved like a 15-minute one — signed out after the
  // shortest coffee break. The cost is one extra failed POST at bootstrap for genuine guests, who
  // have no refresh cookie for it to find.
  const isRefreshExempt = reqUrl.startsWith('auth/refresh-token')
    || reqUrl.startsWith('auth/session') || reqUrl.startsWith('auth/google');

  const endSession = () => {
    tokenStorageService.clearUser();
    serviceTokenService.clear();
    subscriberService.isUserLoggedIn.next(false);
  };

  const report = (err: HttpErrorResponse) => {
    if (!skipErrorToast) {
      if (err.status === 500) {
        toastService.showError(CommonConstants.INTERNAL_SERVER_ERROR);
      } else {
        // Centralized extraction in ToastService handles all known shapes
        // (401, 422, 400, 403, validation arrays, etc.) and falls back to
        // GENERIC_ERROR_MESSAGE when no usable message is present.
        toastService.showError(err);
      }
    }
    if (err.status === 401) {
      // The session is genuinely over — refresh was either not applicable or was refused.
      endSession();
    }
    return throwError(() => err);
  };

  /**
   * `canRetry` is false on the replay, so a request is attempted at most twice. Without that a 401
   * on the retry would refresh again and recurse until the stack gave out.
   */
  const handle = (request: HttpRequest<any>, canRetry = true): ReturnType<HttpInterceptorFn> => (
    isPlatformBrowser(platformId) ? next(request) : next(request).pipe(timeout(SSR_REQUEST_TIMEOUT_MS))
  ).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }

      const refreshable = err.status === 401
        && canRetry
        && !isRefreshExempt
        && isPlatformBrowser(platformId);

      if (!refreshable) {
        return report(err);
      }

      // A 401 here is the ordinary end of one access token, not the end of the session. Renew once
      // (shared with every other request that 401'd in the same burst — see AuthRefreshService) and
      // replay. Only if the server refuses the renewal is the session actually over.
      return authRefreshService.refresh().pipe(
        switchMap((renewed) => renewed ? handle(withCsrf(request), false) : report(err))
      );
    })
  );

  /**
   * Renew BEFORE the access cookie lapses, not only after a 401.
   *
   * Public ('?') endpoints never answer 401. Once the ~15-minute access cookie lapses the browser
   * stops sending it, and an endpoint that also serves guests — `product` above all — answers 200 AS
   * a guest, silently leaving out what guests may not see (`price`, `rrp_price`). With nothing for
   * `handle` to react to, a signed-in customer who opened a product page a quarter of an hour into
   * a visit saw "Trade Rate:" with no amount until a reload ran the boot /me probe, which does 401
   * and so renewed. Renewing ahead of the expiry closes that for every such endpoint at once.
   *
   * When the expiry is unknown (ServiceTokenService) this is a no-op and only the 401 path applies.
   * Concurrent requests share the one refresh (AuthRefreshService), as on the 401 path.
   *
   * Early renewal can still miss — no expiry known (the service-token fetch failed), or a browser
   * clock running behind the API's — so a price answer that comes back as a guest's is caught here
   * too (`servedAsGuest`): renew, then ask once more.
   */
  const send = (request: HttpRequest<any>): ReturnType<HttpInterceptorFn> => {
    const renewFirst = isPlatformBrowser(platformId)
      && !isRefreshExempt
      && !reqUrl.startsWith('auth/logout')
      && tokenStorageService.checkUserLoggedIn()
      && serviceTokenService.isAccessTokenDue();
    const sent = !renewFirst ? handle(request) : authRefreshService.refresh().pipe(
      switchMap((renewed) => {
        if (renewed) {
          return handle(withCsrf(request));
        }
        // Refused: the session is over. Drop the signed-in state first so the request goes out as the
        // guest it now is — a public read still answers, a protected one reports its own 401.
        endSession();
        return handle(request, false);
      })
    );
    return sent.pipe(
      switchMap((event) => servedAsGuest(event) ? askAgainSignedIn(request, event) : of(event))
    );
  };

  // The `fields` this request asks for that end in `price`, each as the path to the rows holding it:
  // 'price' -> [], 'products.price' -> ['products']. Empty unless it is a GET for product data.
  const pricePaths: string[][] = req.method !== 'GET' || !reqUrl.startsWith('product') ? [] :
    (new URLSearchParams(reqUrl.split('?')[1] ?? '').get('fields') ?? '').split(',')
      .map((field) => field.trim().split('.'))
      .filter((path) => path.length > 0 && path[path.length - 1] === 'price')
      .map((path) => path.slice(0, -1));

  /**
   * Whether a signed-in browser asked for trade prices and was answered as a GUEST.
   *
   * The API leaves guest-hidden fields out entirely (Product::fields), while a signed-in answer always
   * carries the key — `null` included — so rows without any `price` key can only be a guest answer.
   * Rows with nothing at a requested path (no results, no products expanded) prove nothing.
   */
  const servedAsGuest = (event: HttpEvent<unknown>): boolean => {
    if (!pricePaths.length || !(event instanceof HttpResponse) || !isPlatformBrowser(platformId)
      || !tokenStorageService.checkUserLoggedIn()) {
      return false;
    }
    const data = (event.body as any)?.data;
    let rowsSeen = false;
    for (const path of pricePaths) {
      let rows: any[] = Array.isArray(data) ? data : (data ? [data] : []);
      for (const key of path) {
        rows = rows.flatMap((row) => row?.[key] ?? []);
      }
      rows = rows.filter((row) => row && typeof row === 'object');
      if (rows.some((row) => 'price' in row)) {
        return false;
      }
      rowsSeen ||= rows.length > 0;
    }
    return rowsSeen;
  };

  /**
   * Renew and ask once more. Refused, the session is over; answered as a guest even with a session
   * just renewed, it no longer counts for anything. Either way the signed-in state is dropped — the
   * page then shows what a guest sees instead of "Trade Rate:" with no amount — and the guest answer
   * stands. The replay is never retried, so this costs at most one refresh per request.
   */
  const askAgainSignedIn = (request: HttpRequest<any>, guestAnswer: HttpEvent<unknown>): ReturnType<HttpInterceptorFn> =>
    authRefreshService.refresh().pipe(
      switchMap((renewed) => {
        if (!renewed) {
          endSession();
          return of(guestAnswer);
        }
        return handle(withCsrf(request), false).pipe(
          tap((event) => {
            if (servedAsGuest(event)) {
              endSession();
            }
          })
        );
      })
    );

  /**
   * Attach the double-submit CSRF header, re-reading the cookie each time.
   *
   * Re-read rather than captured once because the replay after a refresh must use whatever the
   * cookie holds NOW. A silent refresh deliberately preserves the existing token (see
   * AuthCookieTrait::setAuthCookies), so in practice it is unchanged — but a concurrent login in
   * another tab does rotate it, and a stale header there is a 403 on a valid request.
   */
  function withCsrf(request: HttpRequest<any>): HttpRequest<any> {
    if (!isPlatformBrowser(platformId) || !['POST', 'PUT', 'PATCH', 'DELETE'].includes(request.method)) {
      return request;
    }
    const token = readCookie(environment.csrfCookieName ?? CSRF_COOKIE_LEGACY) ?? readCookie(CSRF_COOKIE_LEGACY);
    return token ? request.clone({setHeaders: {'X-XSRF-TOKEN': token}}) : request;
  }

  if (isNodeAPI) {
    // Same HttpOnly cookie flow as the V3 API: send credentials + the X-Auth-Client hint so the
    // Node API (v1) reads the auth cookie exactly like V3. No Authorization header — the browser
    // attaches the cookie automatically. NOTE: this requires the cookie to reach the Node host;
    // that holds locally (shared localhost) but in staging/prod the Node API is a different host,
    // so the auth cookie must be issued with a shared parent domain there.
    //
    // The CSRF header goes to the Node API TOO, and must. This branch used to omit it, on the
    // reasoning that the Node API did not check. That stopped being true when authentication moved
    // across: `auth/refresh-token`, `auth/logout` and `auth/change-password` all enforce the
    // double-submit check there now, and enforcement keys off the presence of a session cookie —
    // which the browser attaches by itself. A signed-in customer therefore sent a cookie and no
    // header and got `403 Invalid or missing CSRF token.` Because RENEWAL is one of those
    // endpoints, the session could never be renewed: it ended at the first access-token expiry,
    // which under the current policy is fifteen minutes.
    //
    // withCsrf is a no-op on GET/HEAD and when no CSRF cookie is readable, so a guest browsing the
    // catalogue is unaffected.
    req = withCsrf(req.clone({withCredentials: true, setHeaders: {'X-Auth-Client': 'frontend'}}));
    return send(req);
  }

  // Same-host gatherco API: send the HttpOnly auth cookie and, for state-changing requests, the
  // double-submit CSRF token. X-Auth-Client scopes the server to the frontend's own cookie so it
  // never falls back to the admin cookie when both apps are logged in on the shared API host.
  // The CSRF cookie is scoped per-client and per-environment (environment.csrfCookieName ->
  // 'XSRF-TOKEN{envSuffix}'). It used to be the bare 'XSRF-TOKEN' for every app and every
  // environment, which at Domain=.gatherco.com.au meant ONE cookie shared by this site, the admin
  // console and staging. Each of them rotates the token when it mints a session, so another app
  // could replace it while a write of ours was already on the wire; the server then saw
  // cookie != header and answered `403 Invalid or missing CSRF token.` on a valid request.
  //
  // The legacy name is read as a FALLBACK so a bundle already running in someone's tab keeps
  // working across the deploy. DELETE the fallback once this build is live everywhere - see the
  // matching TRANSITION note on the API's AuthCookieTrait::csrfAcceptedCookieNames().
  req = withCsrf(req.clone({withCredentials: true, setHeaders: {'X-Auth-Client': 'frontend'}}));

  return send(req);
};
